import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldOff, EyeOff, RotateCcw, Loader2, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";

/**
 * Admin-only card showing approval status, delisting, subscription state,
 * and override toggle. Mounted as a suffix slot in the admin profile pages.
 */
export const AdminStatusCard = ({
  data,
  busy,
  onApprove,
  onReject,
  onDelistToggle,
  onOverrideToggle,
  onMarkReviewed,
  approvalKey = "approval_status", // 'approval_status' for markets, 'status' for businesses
}) => {
  const approval = data?.[approvalKey] || "approved";
  const isDelisted = !!data?.is_delisted;
  const subActive = !!data?.subscription_active;
  const overrideOn = !!data?.admin_subscription_override;
  const reviewNeeded = !!data?.profile_review_needed;

  return (
    <Card className="col-span-1 md:col-span-12 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="admin-status-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#42D78A]" /> Admin Controls
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Approval */}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Approval</p>
            <div className="flex items-center gap-2 mb-3">
              {approval === "pending" && <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>}
              {approval === "approved" && <Badge className="bg-emerald-100 text-emerald-800">Approved</Badge>}
              {approval === "rejected" && <Badge className="bg-red-100 text-red-800">Rejected</Badge>}
            </div>
            {(approval === "pending" || approval === "rejected") && (
              <div className="flex gap-2">
                <Button onClick={onApprove} disabled={busy} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="admin-approve-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</>}
                </Button>
                {approval === "pending" && (
                  <Button onClick={onReject} disabled={busy} size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="admin-reject-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Listing visibility */}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Directory Visibility</p>
            <div className="flex items-center gap-2 mb-3">
              {isDelisted ? <Badge className="bg-orange-100 text-orange-800">Delisted</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Visible</Badge>}
            </div>
            <Button onClick={onDelistToggle} disabled={busy} size="sm" variant="outline" className="rounded-full" data-testid="admin-delist-toggle-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isDelisted ? <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Relist</> : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Delist</>}
            </Button>
          </div>

          {/* Profile review */}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Profile Edits</p>
            <div className="flex items-center gap-2 mb-3">
              {reviewNeeded ? (
                <Badge className="bg-amber-100 text-amber-800">
                  <AlertTriangle className="mr-1 h-3 w-3" /> Needs Review
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-700">No New Edits</Badge>
              )}
            </div>
            {reviewNeeded && data?.profile_review_requested_at && (
              <p className="text-xs text-slate-500">
                Edited {new Date(data.profile_review_requested_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
            )}
            {reviewNeeded && onMarkReviewed && (
              <Button onClick={onMarkReviewed} disabled={busy} size="sm" variant="outline" className="mt-3 rounded-full" data-testid="admin-mark-reviewed-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Mark Reviewed</>}
              </Button>
            )}
          </div>

          {/* Subscription */}
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Subscription</p>
            <div className="flex items-center gap-2 mb-3">
              {subActive ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                : overrideOn ? <Badge className="bg-blue-100 text-blue-800">Override</Badge>
                : <Badge className="bg-gray-100 text-gray-700">No Sub</Badge>}
              {data?.subscription_paid_until && subActive && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" />until {new Date(data.subscription_paid_until).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
              )}
            </div>
            {!subActive && (
              <Button onClick={onOverrideToggle} disabled={busy} size="sm" variant="outline" className="rounded-full" data-testid="admin-override-toggle-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : overrideOn ? <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Remove Override</> : <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Enable Override</>}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
